import { useMarketLocationId, useTokenInfo } from '@e2m-customer-portal/hooks';
import { Dialog, getErrorCode, userIds } from '@e2m-customer-portal/ui';
import { Button, DialogContent } from '@mui/material';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorInfoIcon } from '../../assets/icons';
import { getErrorTranslationKey, getRequestId } from '../../utils';
import { getCoptErrorTranslationString } from './utils';

type cOptRequestErrorType = {
  detail:
    | [
        {
          type: string;
          loc: Array<string>;
          msg: string;
          input: number;
          ctx: Record<string, number>;
        },
      ]
    | string;
};

interface ErrorDialogProps {
  open: boolean;
  error: string | Error;
  onClose: () => void;
}

export function ErrorDialog({ open, error, onClose }: Readonly<ErrorDialogProps>): ReactElement<ErrorDialogProps> {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const date = useMemo(() => new Date().toISOString(), [error]);
  const requestId = getRequestId(error);
  const marketLocationId = useMarketLocationId();
  const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;
  const { email, roles } = useTokenInfo();
  const userId =
    roles && Object.keys(userIds).includes([...roles][0])
      ? userIds[[...roles][0] as unknown as userIds].valueOf()
      : undefined;

  const errorMessage = (error as Error).message;
  const errorCode = getErrorCode(error);
  const parsedError: cOptRequestErrorType = errorMessage && errorCode === 'A502' && JSON.parse(errorMessage);

  return (
    <Dialog
      open={open}
      title={
        <div className="inline-flex gap-3 text-error-500">
          <ErrorInfoIcon className="m-0 mt-0.5 w-6 min-w-6" />
          {error instanceof Error ? t(getErrorTranslationKey(error)) : error}
        </div>
      }
      actions={
        <Button className="ml-auto" onClick={onClose}>
          {t('SHARED_OK')}
        </Button>
      }
      onClose={onClose}
    >
      <DialogContent className="flex flex-col gap-y-4 px-12">
        {errorCode === 'A502' && parsedError ? (
          typeof parsedError.detail === 'string' ? (
            <div className="text-sm font-medium">{parsedError.detail}</div>
          ) : (
            parsedError.detail.map((detail) => {
              const errorType = getCoptErrorTranslationString(detail.type);
              return (
                <div key={`${detail.type}-${detail.input}`} className="p-2 bg-warning/40 ">
                  <div className="text-sm font-semibold">
                    {t(
                      `COPT_${detail.loc
                        .slice(2)
                        .filter((item) => typeof item === 'string')

                        .join('_')
                        .replace(UUID_REGEX, '')
                        .replace(/battery/i, 'BATTERIES')
                        .toUpperCase()}`,
                    )}
                    :
                  </div>
                  <div className="text-sm font-medium">
                    {errorType
                      ? tv(`${errorType.string}`, { value: errorType.code ? detail.ctx[errorType.code] : undefined })
                      : `${detail.msg}`}
                  </div>
                </div>
              );
            })
          )
        ) : (
          <div className="text-error-500">
            {errorCode === 'A504'
              ? t('SCHEDULING_ERROR_FORECAST_GAP_INSTRUCTIONS')
              : t('SHARED_ERROR_DIALOG_INSTRUCTIONS')}
          </div>
        )}
        <ul className="m-0 list-none p-0 text-sm text-grey-400">
          <li>{t('SHARED_ERROR_CODE', { code: errorCode })}</li>
          {requestId && <li>{t('SHARED_ERROR_REQUEST', { id: requestId })}</li>}
          {userId && <li>{t('SHARED_ERROR_USER_TYPE', { id: userId })}</li>}
          {email && <li>{t('SHARED_ACCOUNT', { email })}</li>}
          {marketLocationId && <li>{t('SHARED_ERROR_MARKET_LOCATION', { id: marketLocationId })}</li>}
          <li>{t('SHARED_ERROR_DATE', { date })}</li>
        </ul>
      </DialogContent>
    </Dialog>
  );
}
